import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import { Container, Graphics, Text, Sprite } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import * as PIXI from "pixi.js";
import { TextMetrics } from "pixi.js";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  closeAutoplayPopup,
  setAutoSpinCount,
  startAutoPlay,
  setStopOnAnyWin,
  setStopOnBonusGame,
  setStopOnSingleWinExceeds,
  setStopOnBalanceIncreases,
  setStopOnBalanceDecreases,
  setSingleWinExceedsAmount,
  setBalanceIncreasesAmount,
  setBalanceDecreasesAmount,
} from "../../../../State/actions";
import {
  selectAutoplayPopupActive,
  selectBet,
  selectBalance,
  selectStopOnAnyWin,
  selectStopOnBonusGame,
  selectStopOnSingleWinExceeds,
  selectStopOnBalanceIncreases,
  selectStopOnBalanceDecreases,
  selectSingleWinExceedsAmount,
  selectBalanceIncreasesAmount,
  selectBalanceDecreasesAmount,
  selectCounterAnimationFinished,
  selectIsSpinning,
  selectCriticalAnimationPlaying,
} from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../pixiSettings";
import { getNewUITexture } from "../../../../assets";
import ButtonMenuClose from "../Buttons/ButtonMenuClose";
import BetButton from "../Buttons/BetButton";
import Checkbox from "../Buttons/Checkbox";
import CheckboxWithInput from "../Buttons/CheckboxWithInput";
import anime from "animejs";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";

/**
 * PopiAutoplayPopup Component
 *
 * Displays a popup for configuring autoplay settings with two main sections:
 * 1. Stop Autospin conditions - checkboxes for stopping conditions (any win, bonus game,
 *    single win exceeds amount, balance increases/decreases by amount)
 * 2. Numbers of Rounds - grid of buttons to select autoplay count (10, 25, 50, etc.)
 *
 * Layout Modes:
 * - Mobile Landscape: Two-column layout (55% checkboxes, 45% buttons) side-by-side
 * - All Other Devices: Stacked layout (checkboxes on top, buttons below)
 * - Tablet Landscape/Desktop: Windowed mode with popup frame
 * - Mobile/Tablet Portrait: Fullscreen mode with dark background
 */

// Available autoplay round count options
const AUTOPLAY_NUMBERS = [10, 25, 50, 100, 250, 500, 750, 1000, Infinity];

/**
 * Responsive popup configuration
 * Base values are in pixels and scaled by uiSymbolScale (fullscreen) or popupScale (windowed)
 * Multipliers adjust sizing per device/orientation combination
 */
const POPUP_CONFIG = {
  // Base dimensions — padding matches BetSelectPopup for consistent content area
  basePadding: {
    horizontal: 28,
    vertical: 40,
  },
  baseTitleFontSize: 42,
  baseTitleGap: 26,
  baseSectionFontSize: 24,
  baseSectionGap: 20,

  // Device/orientation-specific multipliers
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 1.4, top: 1.5, bottom: 0.5 },
      titleFontMultiplier: 1.8,
      titleGapMultiplier: 0,
      sectionFontMultiplier: 2.35,
      sectionGapMultiplier: 2.0,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.8, top: 2.6, bottom: 0.75 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 2.0,
      sectionFontMultiplier: 2.5,
      sectionGapMultiplier: 2.0,
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 1.4, v: 6.75 },
      titleFontMultiplier: 1.8,
      titleGapMultiplier: 0,
      sectionFontMultiplier: 2.35,
      sectionGapMultiplier: 2.0,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 5.0, top: 2.75, bottom: 0.75 },
      titleFontMultiplier: 1.9,
      titleGapMultiplier: 1.5,
      sectionFontMultiplier: 2.5,
      sectionGapMultiplier: 2.0,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 8.0, v: 7.25 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 2.25,
      sectionFontMultiplier: 3.0,
      sectionGapMultiplier: 2.5,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 0.75 },
      titleFontMultiplier: 0.9,
      titleGapMultiplier: 0.75,
      sectionFontMultiplier: 1.2,
      sectionGapMultiplier: 0.75,
      // Windowed mode positioning offsets (as multipliers of popup size)
      positionOffsetRight: 0.95, // Offset from right edge
      positionXDivisor: 9.0, // Divisor for toolbar.width X offset
      positionOffsetBottom: 2.25, // Offset from bottom (in closeButtonConfig.height units)
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 0.75 },
      titleFontMultiplier: 1.0,
      titleGapMultiplier: 0.5,
      sectionFontMultiplier: 1.2,
      sectionGapMultiplier: 0.75,
      // Windowed mode positioning offsets (as multipliers of popup size)
      positionOffsetRight: 1.25, // Offset from right edge
      positionXDivisor: 4.3, // Divisor for toolbar.width X offset
      positionOffsetBottom: 2.25, // Offset from bottom (in closeButtonConfig.height units)
    },
  },
};

// ── Overlay constants ─────────────────────────────────────────────────────
const OVERLAY_ALPHA = 1.0;
const OVERLAY_FADE_MS = 200;

const AutoplayPopup = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // UI State
  const popupActive = useSelector(selectAutoplayPopupActive);
  const balance = useSelector(selectBalance);
  const bet = useSelector(selectBet);

  // Guards - prevent starting new spin if ongoing spin, win lines displaying, or critical animation
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const spinComplete = useSelector((state) => state.spin.spinComplete);
  const currentRound = useSelector((state) => state.game.currentRound);

  // Autoplay stop conditions state (responsible gambling features)
  const stopOnAnyWin = useSelector(selectStopOnAnyWin);
  const stopOnBonusGame = useSelector(selectStopOnBonusGame);
  const stopOnSingleWinExceeds = useSelector(selectStopOnSingleWinExceeds);
  const stopOnBalanceIncreases = useSelector(selectStopOnBalanceIncreases);
  const stopOnBalanceDecreases = useSelector(selectStopOnBalanceDecreases);
  const singleWinExceedsValue = useSelector(selectSingleWinExceedsAmount);
  const balanceIncreasesValue = useSelector(selectBalanceIncreasesAmount);
  const balanceDecreasesValue = useSelector(selectBalanceDecreasesAmount);

  // Mutual exclusivity: "On any win" vs "balance increases", "balance decreases", "bonus game"
  // Note: "If single win exceeds" CAN coexist with "On any win" per spec
  const handleOnAnyWinChange = useCallback(
    (checked) => {
      dispatch(setStopOnAnyWin(checked));
      if (checked) {
        // Uncheck mutually exclusive options (not single win exceeds — it can coexist)
        dispatch(setStopOnBonusGame(false));
        dispatch(setStopOnBalanceIncreases(false));
        dispatch(setBalanceIncreasesAmount(""));
        dispatch(setStopOnBalanceDecreases(false));
        dispatch(setBalanceDecreasesAmount(""));
        // Clear any focused input for balance fields since those checkboxes are now off
        setFocusedInput((prev) =>
          prev === "balanceIncreases" || prev === "balanceDecreases"
            ? null
            : prev,
        );
      }
    },
    [dispatch],
  );

  const handleSingleWinChange = useCallback(
    (checked) => {
      dispatch(setStopOnSingleWinExceeds(checked));
      if (!checked) {
        dispatch(setSingleWinExceedsAmount(""));
        setFocusedInput((prev) => (prev === "singleWinExceeds" ? null : prev));
      }
    },
    [dispatch],
  );

  const handleBalanceIncreasesChange = useCallback(
    (checked) => {
      dispatch(setStopOnBalanceIncreases(checked));
      if (checked) {
        dispatch(setStopOnAnyWin(false));
      }
      if (!checked) {
        dispatch(setBalanceIncreasesAmount(""));
        setFocusedInput((prev) => (prev === "balanceIncreases" ? null : prev));
      }
    },
    [dispatch],
  );

  const handleBonusGameChange = useCallback(
    (checked) => {
      dispatch(setStopOnBonusGame(checked));
      if (checked) {
        dispatch(setStopOnAnyWin(false));
      }
    },
    [dispatch],
  );

  // Responsive dimensions and device info
  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
    getResponsiveConfig,
    isMobileSmall,
  } = useDimensions();

  // Refs for animation
  const containerRef = useRef(null);

  // Animation state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  // Input focus management (only one input can be focused at a time)
  const [focusedInput, setFocusedInput] = React.useState(null);

  // Basic/Advanced mode ref (only for windowed mode) - using ref to avoid re-render triggers
  const isAdvancedMode = useRef(false);

  // Force re-render when toggling mode - store state value for dependency tracking
  const [modeUpdateCounter, forceUpdate] = React.useReducer((x) => x + 1, 0);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    // Dismiss the on-screen keyboard immediately when the popup closes.
    // HiddenAutoplayInputs fights blur for ~1s (its iOS anti-blur refocus
    // burst), so a plain blur() gets undone and the keyboard lingers into the
    // main game. Turn that guard OFF first, then blur the focused input.
    const refs = window.autoplayInputRefs;
    if (refs?.shouldMaintainFocusRef) {
      refs.shouldMaintainFocusRef.current = false;
    }
    const active = document.activeElement;
    if (
      active &&
      (active.tagName === "INPUT" || active.tagName === "TEXTAREA")
    ) {
      active.blur();
    }
    setIsClosing(true);
  }, [isClosing]);

  // Determine display mode: windowed (with frame) for tablet landscape/desktop, fullscreen otherwise - memoized
  const showPopupFrame = useMemo(
    () =>
      (device === PixiSettings.DEVICE.TABLET &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE) ||
      device === PixiSettings.DEVICE.DESKTOP,
    [device, orientation],
  );

  // Toggle between Basic and Advanced mode
  const handleModeToggle = useCallback(() => {
    if (!showPopupFrame) return; // Only for windowed mode

    // Prevent toggle if ongoing spin, win lines displaying, or critical animation
    const hasWin = currentRound?.win && currentRound.win > 0;
    const winNotFullyDisplayed =
      hasWin && spinComplete && !counterAnimationFinished;

    if (isSpinning || winNotFullyDisplayed || criticalAnimationPlaying) {
      return;
    }

    soundManager.play(SOUND_NAME.button_click);

    // Toggle the mode and force re-render (instant, no animation)
    isAdvancedMode.current = !isAdvancedMode.current;
    forceUpdate();
  }, [
    showPopupFrame,
    forceUpdate,
    isSpinning,
    criticalAnimationPlaying,
    currentRound,
    spinComplete,
    counterAnimationFinished,
  ]);

  // Calculate button aspect ratio from texture for natural sizing - memoized
  const buttonBackgroundTexture = useMemo(
    () => getNewUITexture("bet_buttons_background"),
    [],
  );
  const buttonAspectRatio = useMemo(
    () =>
      buttonBackgroundTexture
        ? buttonBackgroundTexture.width / buttonBackgroundTexture.height
        : 2.63,
    [buttonBackgroundTexture],
  );

  // Triangle texture for Basic/Advanced toggle button - memoized
  const triangleTexture = useMemo(() => getNewUITexture("triangle"), []);

  // Select appropriate popup frame texture based on display mode and Basic/Advanced state - memoized
  // Basic mode (default): Uses horizontal frame for windowed, Advanced uses vertical
  // Note: Uses modeUpdateCounter to ensure recalculation when mode changes via ref
  const popupFrameTexture = useMemo(() => {
    // Read ref value directly - modeUpdateCounter ensures recalculation on mode change
    const isAdvanced = isAdvancedMode.current;
    return getNewUITexture(
      showPopupFrame
        ? isAdvanced
          ? "popup_autospin_settings_advanced"
          : "popup_autospin_settings_basic"
        : "popup_settings",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopupFrame, modeUpdateCounter]);

  const isMobilePortraitFullscreen = useMemo(
    () =>
      !showPopupFrame &&
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT,
    [showPopupFrame, device, orientation],
  );

  const isMobileSmallPortraitFullscreen = useMemo(
    () => isMobileSmall && isMobilePortraitFullscreen,
    [isMobileSmall, isMobilePortraitFullscreen],
  );

  const isMobileSmallLandscapeFullscreen = useMemo(
    () =>
      isMobileSmall &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE &&
      !showPopupFrame,
    [isMobileSmall, orientation, showPopupFrame],
  );

  /**
   * Calculate popup dimensions and scale
   * Windowed mode: Uses texture size scaled by device-specific multiplier
   * Fullscreen mode: Uses full viewport dimensions
   */
  const popupSize = useMemo(() => {
    if (!popupFrameTexture) return { width: 0, height: 0, scale: 1 };

    if (showPopupFrame) {
      const sizeMultiplier =
        device === PixiSettings.DEVICE.DESKTOP ? 0.8 : 1.25;
      const finalScale = uiSymbolScale * sizeMultiplier;
      return {
        width: popupFrameTexture.width * finalScale,
        height: popupFrameTexture.height * finalScale,
        scale: finalScale,
      };
    }

    // Fullscreen: cap width to toolbar width so content stays within game area
    // Mobile small landscape: use full screen width so columns reach the edges
    const maxWidth = isMobileSmallLandscapeFullscreen
      ? dimensions.width
      : toolbar?.width
        ? Math.min(dimensions.width, toolbar.width)
        : dimensions.width;
    return {
      width: maxWidth,
      height: dimensions.height,
      scale: uiSymbolScale,
    };
  }, [
    popupFrameTexture,
    showPopupFrame,
    dimensions,
    uiSymbolScale,
    device,
    toolbar,
    isMobileSmallLandscapeFullscreen,
  ]);

  const popupScale = popupSize.scale;

  /**
   * Calculate viewport configuration for content area
   * Accounts for padding, title space, and device-specific multipliers
   * Returns null if textures aren't loaded yet
   */
  const viewportConfig = useMemo(() => {
    if (!popupFrameTexture || popupSize.width === 0) return null;

    const configDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const config = POPUP_CONFIG[configDevice]?.[orientation] ||
      POPUP_CONFIG[device]?.[orientation] || {
        paddingMultiplier: { h: 1.0, v: 1.0 },
        titleFontMultiplier: 1.0,
        titleGapMultiplier: 1.0,
        sectionFontMultiplier: 1.0,
        sectionGapMultiplier: 1.0,
      };

    if (showPopupFrame) {
      // Windowed mode: scale with popupScale — matches BetSelectPopup padding
      const paddingH =
        (POPUP_CONFIG.basePadding.horizontal *
          config.paddingMultiplier.h *
          popupScale) /
        2;
      const paddingV =
        POPUP_CONFIG.basePadding.vertical *
        config.paddingMultiplier.v *
        popupScale;
      const contentWidth = popupSize.width - paddingH * 2;
      const contentHeight = popupSize.height - paddingV * 2;
      const titleFontSize =
        Math.round(
          (POPUP_CONFIG.baseTitleFontSize *
            config.titleFontMultiplier *
            popupScale) /
            2,
        ) *
          2 -
        4;
      const titleGap =
        POPUP_CONFIG.baseTitleGap * config.titleGapMultiplier * popupScale;
      const titleYOffset = titleFontSize + titleGap;

      return {
        maxWidth: contentWidth,
        maxHeight: contentHeight,
        x: -popupSize.width / 2 + paddingH,
        y: -popupSize.height / 2 + paddingV,
        titleFontSize,
        titleYOffset,
        viewportHeight: contentHeight - titleYOffset,
        sectionFontSize: Math.round(
          POPUP_CONFIG.baseSectionFontSize *
            config.sectionFontMultiplier *
            popupScale,
        ),
        sectionGap:
          POPUP_CONFIG.baseSectionGap *
          config.sectionGapMultiplier *
          popupScale,
      };
    }

    // Fullscreen mode: scale with uiSymbolScale
    const paddingH =
      POPUP_CONFIG.basePadding.horizontal *
      config.paddingMultiplier.h *
      uiSymbolScale;
    const paddingTopMult =
      config.paddingMultiplier.top ?? config.paddingMultiplier.v ?? 1.0;
    const paddingBottomMult =
      config.paddingMultiplier.bottom ?? config.paddingMultiplier.v ?? 1.0;
    const paddingTop =
      POPUP_CONFIG.basePadding.vertical * paddingTopMult * uiSymbolScale;
    const paddingBottom =
      POPUP_CONFIG.basePadding.vertical * paddingBottomMult * uiSymbolScale;
    const contentWidth = popupSize.width - paddingH * 2;
    const contentHeight = dimensions.height - paddingTop - paddingBottom;
    let titleFontSize =
      Math.round(
        (POPUP_CONFIG.baseTitleFontSize *
          config.titleFontMultiplier *
          uiSymbolScale) /
          2,
      ) * 2;
    if (isMobileSmallPortraitFullscreen) {
      titleFontSize = Math.round((titleFontSize * 1.1) / 2) * 2;
    }
    const titleGap =
      POPUP_CONFIG.baseTitleGap * config.titleGapMultiplier * uiSymbolScale;
    const titleYOffset = titleFontSize + titleGap;

    let sectionFontSize = Math.round(
      POPUP_CONFIG.baseSectionFontSize *
        config.sectionFontMultiplier *
        uiSymbolScale,
    );
    if (isMobileSmallPortraitFullscreen) {
      sectionFontSize = Math.round((sectionFontSize * 1.1) / 2) * 2;
    }

    return {
      maxWidth: contentWidth,
      maxHeight: contentHeight,
      x: -popupSize.width / 2 + paddingH,
      y: -dimensions.height / 2 + paddingTop,
      titleFontSize,
      titleYOffset,
      viewportHeight: contentHeight - titleYOffset,
      sectionFontSize,
      sectionGap:
        POPUP_CONFIG.baseSectionGap *
        config.sectionGapMultiplier *
        uiSymbolScale,
    };
  }, [
    popupSize,
    dimensions,
    popupFrameTexture,
    showPopupFrame,
    popupScale,
    uiSymbolScale,
    device,
    orientation,
    isMobileSmall,
    isMobileSmallPortraitFullscreen,
  ]);

  // Memoize translation texts - prevents recalculation on every render
  const titleText = useMemo(() => t("ui_autospins_title"), [t]);
  const stopAutospinText = useMemo(() => t("ui_autospins_stop_autospin"), [t]);
  const numbersOfRoundsText = useMemo(
    () => t("ui_autospins_numbers_of_rounds"),
    [t],
  );
  const basicText = useMemo(() => t("ui_autospins_basic"), [t]);
  const advancedText = useMemo(() => t("ui_autospins_advanced"), [t]);

  // Auto-scaling title font size
  const finalTitleFontSize = useMemo(() => {
    if (!viewportConfig) return 48;

    const idealFontSize = viewportConfig.titleFontSize;

    // In windowed mode, reserve space for mode toggle button + close button
    let maxTextWidth;
    if (showPopupFrame) {
      maxTextWidth = viewportConfig.maxWidth * 0.55; // ~45% reserved for toggle + close
    } else {
      maxTextWidth = viewportConfig.maxWidth * 0.8;
    }

    // Measure text with ideal font size
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "500",
      align: "center",
    });
    const metrics = TextMetrics.measureText(titleText, tempStyle);

    // Scale down if text exceeds max width
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    return idealFontSize;
  }, [viewportConfig, titleText, showPopupFrame]);

  // Auto-scaling section heading font size
  const finalSectionFontSize = useMemo(() => {
    if (!viewportConfig) return 24;

    const longerText =
      stopAutospinText.length > numbersOfRoundsText.length
        ? stopAutospinText
        : numbersOfRoundsText;
    const idealFontSize = viewportConfig.sectionFontSize;
    const maxTextWidth = viewportConfig.maxWidth * 0.9; // 90% of content width

    // Measure text with ideal font size
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "400",
      align: "left",
    });
    const metrics = TextMetrics.measureText(longerText, tempStyle);

    // Scale down if text exceeds max width
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    return idealFontSize;
  }, [viewportConfig, stopAutospinText, numbersOfRoundsText]);

  // Text styles for title and section headings
  const dpr = window.devicePixelRatio || 1;
  const titleStyle = useMemo(
    () =>
      viewportConfig &&
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalTitleFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        resolution: dpr,
      }),
    [viewportConfig, finalTitleFontSize, dpr],
  );

  const sectionHeadingStyle = useMemo(
    () =>
      viewportConfig &&
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalSectionFontSize,
        fontWeight: "400",
        fill: 0xffffff,
        align: "left",
        resolution: dpr,
      }),
    [viewportConfig, finalSectionFontSize, dpr],
  );

  // Layout mode: two-column side-by-side for mobile landscape, stacked for others
  const useTwoColumnCheckboxLayout = useMemo(
    () =>
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE,
    [device, orientation],
  );

  // Two-column width split and gap — mobile landscape
  const twoColumnLayoutConfig = useMemo(
    () => ({ leftRatio: 0.58, rightRatio: 0.4, gapMultiplier: 3.5 }),
    [],
  );

  // Gap between columns in two-column layout (mobile landscape)
  const twoColumnGap = useMemo(() => {
    if (!viewportConfig || !useTwoColumnCheckboxLayout) return 0;
    return viewportConfig.sectionGap * twoColumnLayoutConfig.gapMultiplier;
  }, [viewportConfig, useTwoColumnCheckboxLayout, twoColumnLayoutConfig]);

  /**
   * Calculate button grid configuration
   * - 5 columns for tablet landscape/desktop, 3 columns for others
   * - Mobile landscape: buttons use 45% width (checkboxes use 55%)
   * - Buttons maintain natural aspect ratio from texture
   */
  const buttonConfig = useMemo(() => {
    if (!viewportConfig)
      return {
        width: 100,
        height: 50,
        fontSize: 18,
        spacing: 10,
        rowSpacing: 10,
        columns: 3,
      };

    const columns =
      (device === PixiSettings.DEVICE.TABLET &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE) ||
      device === PixiSettings.DEVICE.DESKTOP
        ? 5
        : useTwoColumnCheckboxLayout
          ? 3
          : 4;
    const gapRatio = useTwoColumnCheckboxLayout ? 0.1 : 0.075;

    // Mobile landscape: buttons fill remaining width after left column + gap
    const twoColumnLeftWidth = useTwoColumnCheckboxLayout
      ? (viewportConfig.maxWidth - twoColumnGap) *
        twoColumnLayoutConfig.leftRatio
      : 0;
    const availableWidth = useTwoColumnCheckboxLayout
      ? viewportConfig.maxWidth - twoColumnLeftWidth - twoColumnGap
      : viewportConfig.maxWidth;

    // Calculate button dimensions maintaining natural aspect ratio
    const buttonWidth = availableWidth / (columns + (columns - 1) * gapRatio);
    const buttonHeight =
      (buttonWidth / buttonAspectRatio) *
      (useTwoColumnCheckboxLayout ? 1.1 : 1);
    const spacing = buttonWidth * gapRatio;
    const rowSpacing = spacing;

    return {
      width: buttonWidth,
      height: buttonHeight,
      fontSize: Math.round(buttonHeight * 0.5),
      spacing,
      rowSpacing,
      columns,
    };
  }, [
    viewportConfig,
    buttonAspectRatio,
    device,
    orientation,
    useTwoColumnCheckboxLayout,
    twoColumnGap,
    twoColumnLayoutConfig,
  ]);

  // ── Open animation (overlay fade → content fade) ─────────────────────────
  useEffect(() => {
    if (popupActive && !isClosing) {
      // If overlay is already fully visible (e.g. orientation change while open),
      // snap to target — don't replay fade-in from 0
      if (overlayAlpha >= OVERLAY_ALPHA) {
        if (containerRef.current) {
          containerRef.current.alpha = 1;
        }
        return;
      }

      // Fade in overlay
      const overlayAnim = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_ALPHA,
        duration: OVERLAY_FADE_MS,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      });

      // Fade in content
      let contentAnim = null;
      if (containerRef.current) {
        containerRef.current.alpha = 0;
        contentAnim = anime({
          targets: containerRef.current,
          alpha: 1,
          duration: 200,
          delay: 100,
          easing: "easeOutQuad",
        });
      }

      return () => {
        overlayAnim.pause();
        if (contentAnim) contentAnim.pause();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupActive, isClosing, showPopupFrame]);

  // ── Close animation (content fade → overlay fade → dispatch) ─────────────
  useEffect(() => {
    if (!isClosing) return;

    let overlayAnim = null;
    let contentAnim = null;

    // Fade out content
    if (containerRef.current) {
      contentAnim = anime({
        targets: containerRef.current,
        alpha: 0,
        duration: 200,
        easing: "easeInQuad",
      });
    }

    // Fade out overlay (slight delay after content)
    overlayAnim = anime({
      targets: { alpha: OVERLAY_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_MS,
      delay: 100,
      easing: "easeInQuad",
      update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      complete: () => {
        setOverlayAlpha(0);
        dispatch(closeAutoplayPopup());
        setIsClosing(false);
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (contentAnim) contentAnim.pause();
    };
  }, [isClosing, dispatch]);

  // Clear focused input when popup closes
  useEffect(() => {
    if (!popupActive && focusedInput !== null) {
      setFocusedInput(null);
    }
  }, [popupActive, focusedInput]);

  // Get responsive button config for close button sizing
  const closeButtonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  /**
   * Calculate popup container position
   * Windowed mode: Positioned above toolbar with configurable offsets
   * Fullscreen mode: Centered on screen
   */
  const popupContainerPosition = useMemo(() => {
    if (showPopupFrame && toolbar && toolbar.width && popupSize.width) {
      if (device === PixiSettings.DEVICE.DESKTOP) {
        // Desktop: derive from the autoplay button's actual position
        // ButtonAutoplay desktop: x = toolbar.width - buttonConfig.width * 4.4, y = toolbar.height / 2
        // Toolbar is centered: absolute x = (dimensions.width - toolbar.width) / 2 + button x
        const toolbarX = (dimensions.width - toolbar.width) / 2;
        const autoplayButtonAbsX =
          toolbarX + toolbar.width - closeButtonConfig.width * 4.4;
        // Y: anchor bottom edge just above toolbar — popup grows upward
        const toolbarTopY = dimensions.height - toolbar.height;
        const gap = toolbar.height * 0.25;
        return {
          x: autoplayButtonAbsX - popupSize.width * 0.6,
          y: toolbarTopY - popupSize.height / 2 - gap,
        };
      }

      // Tablet landscape: anchor bottom edge just above toolbar
      const config = POPUP_CONFIG[device]?.[orientation] || {
        positionOffsetRight: 1.25,
        positionOffsetBottom: 2.25,
      };
      const toolbarTopY = dimensions.height - toolbar.height;
      const gap = toolbar.height * 0.25;
      return {
        x:
          dimensions.width / 2 +
          toolbar.width / (config.positionXDivisor || 4.3),
        y: toolbarTopY - popupSize.height / 2 - gap,
      };
    }
    return {
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    };
  }, [
    showPopupFrame,
    dimensions,
    toolbar,
    popupSize,
    closeButtonConfig,
    device,
    orientation,
  ]);

  /**
   * Calculate close button position
   * Aligned with title at right edge of content area
   * Accounts for button anchor (0.5) and viewport padding
   */
  const closeButtonSizeMultiplier = useMemo(() => {
    if (showPopupFrame) return 0.35;
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE
    )
      return 0.5;
    if (isMobileSmallPortraitFullscreen) return 0.36 * 1.12;
    return 0.36;
  }, [showPopupFrame, device, orientation, isMobileSmallPortraitFullscreen]);

  // Local Y inside content viewport — same visual spot as before (title row).
  const closeButtonLocalY = useMemo(() => {
    const buttonSize = closeButtonConfig.width * closeButtonSizeMultiplier;
    if (showPopupFrame) {
      return (closeButtonConfig.height * closeButtonSizeMultiplier) / 2;
    }
    return buttonSize / 2 - 2;
  }, [closeButtonConfig, closeButtonSizeMultiplier, showPopupFrame]);

  /**
   * Calculate checkbox and input field configuration
   * Checkbox size based on close button, inputs maintain 2.5:1 aspect ratio
   */
  const checkboxConfig = useMemo(() => {
    if (!viewportConfig)
      return {
        size: 0,
        labelFontSize: 18,
        spacing: 30,
        xOffset: 0,
        inputWidth: 0,
        inputHeight: 0,
        stackedSizeMultiplier: 0.65,
        twoColumnCheckboxSizeMultiplier: 0.7,
        twoColumnInputSizeMultiplier: 0.8,
      };

    const mobileSmallCheckboxBoost = isMobileSmallPortraitFullscreen
      ? 1.15
      : useTwoColumnCheckboxLayout
        ? 1.35
        : 1;
    const mobileSmallInputBoost = isMobileSmallPortraitFullscreen
      ? 1.15
      : useTwoColumnCheckboxLayout
        ? 1.25
        : 1;

    const checkboxSize =
      closeButtonConfig.width * 0.7 * mobileSmallCheckboxBoost;
    const spacingMultiplier = showPopupFrame ? 2.0 : 1.5;
    const spacing = checkboxSize * spacingMultiplier;
    const xOffset = checkboxSize / 2; // Account for checkbox anchor 0.5

    const inputHeight = closeButtonConfig.width * 0.7 * mobileSmallInputBoost;
    const inputWidth = inputHeight * 2.25;

    // ── Perfect label font size from longest label ──
    // Layout per row: [checkbox] —gap1— [label text] —gap2— [input field]
    // We measure "If cash balance decreases by" (the longest) and find
    // the exact font size that fills the available text space.
    const cwSizeMultiplier = useTwoColumnCheckboxLayout ? 0.8 : 0.75;
    const cwCheckboxSize =
      closeButtonConfig.width * cwSizeMultiplier * mobileSmallCheckboxBoost;
    const labelX = cwCheckboxSize * 1.0; // gap from checkbox center to text start
    const gapAfterText = cwCheckboxSize * 0.5; // gap between text end and input

    let effectiveContainerWidth;
    if (useTwoColumnCheckboxLayout) {
      effectiveContainerWidth =
        (viewportConfig.maxWidth - twoColumnGap) *
          twoColumnLayoutConfig.leftRatio -
        xOffset;
    } else {
      effectiveContainerWidth = viewportConfig.maxWidth - xOffset;
    }

    const availableTextWidth =
      effectiveContainerWidth - labelX - inputWidth - gapAfterText;

    // Measure ALL checkbox labels in the current language and pick the widest
    // (different languages have different longest labels)
    const allCheckboxLabels = [
      t("ui_autospins_if_single_win"),
      t("ui_autospins_if_increases"),
      t("ui_autospins_if_decreases"),
    ];
    const refSize = 100;
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: refSize,
      fontWeight: "400",
      wordWrap: false,
    });
    // Find the label with the widest pixel width at reference size
    let widestWidth = 0;
    for (const lbl of allCheckboxLabels) {
      const m = TextMetrics.measureText(lbl, tempStyle);
      if (m.width > widestWidth) widestWidth = m.width;
    }
    const metrics = { width: widestWidth };

    let labelFontSize;
    if (availableTextWidth > 0 && metrics.width > 0) {
      labelFontSize =
        Math.round((refSize * (availableTextWidth / metrics.width)) / 2) * 2;
    } else {
      labelFontSize =
        Math.round(
          (viewportConfig.sectionFontSize * (showPopupFrame ? 1.0 : 0.75)) / 2,
        ) * 2;
    }

    return {
      size: checkboxSize,
      labelFontSize,
      spacing,
      xOffset,
      inputWidth,
      inputHeight,
      stackedSizeMultiplier: 0.65 * mobileSmallCheckboxBoost,
      twoColumnCheckboxSizeMultiplier: 0.7 * mobileSmallCheckboxBoost,
      twoColumnInputSizeMultiplier: 0.8 * mobileSmallInputBoost,
    };
  }, [
    viewportConfig,
    closeButtonConfig,
    showPopupFrame,
    useTwoColumnCheckboxLayout,
    twoColumnGap,
    twoColumnLayoutConfig,
    isMobileSmallPortraitFullscreen,
    t,
  ]);

  // Mode toggle button configuration (Basic/Advanced)
  // Slightly bigger than input fields with more text padding and triangle indicator
  const modeButtonConfig = useMemo(() => {
    if (!showPopupFrame || !checkboxConfig) return null;

    // Make button bigger: 1.2x height and 1.6x width (expanded for triangle)
    const buttonHeight = checkboxConfig.inputHeight * 1.2;
    const buttonWidth = checkboxConfig.inputWidth * 1.6;

    // Triangle configuration
    const triangleSize = buttonHeight * 0.2; // Triangle size relative to button height
    const triangleGap = buttonHeight * 0.1; // Small gap between text and triangle

    // Auto-scale font size based on text width
    // Reduced font size to create bigger gap between text and triangle
    const longerText = advancedText; // Use longer text for calculation
    const idealFontSize = Math.round(buttonHeight * 0.5);
    // Calculate available width: button width minus padding, triangle space, and gap
    const buttonPadding = buttonWidth * 0.1; // 10% padding on each side
    const textAreaWidth =
      buttonWidth - buttonPadding * 2 - triangleSize - triangleGap;
    const maxTextWidth = textAreaWidth * 0.95; // 95% of available text area (less aggressive)

    // Measure text width with ideal font size
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "400",
    });
    const metrics = PIXI.TextMetrics.measureText(longerText, tempStyle);

    // Scale down if text exceeds max width
    let fontSize = idealFontSize;
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      fontSize = Math.round(idealFontSize * scale);
    }

    // Calculate text X position: center of the text area (left side of button)
    const textAreaCenterX =
      -buttonWidth / 2 + buttonPadding + textAreaWidth / 2;

    return {
      width: buttonWidth,
      height: buttonHeight,
      fontSize,
      triangleSize,
      textX: textAreaCenterX,
      style: new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize,
        fontWeight: "400",
        fill: 0xffffff,
        align: "center",
        resolution: dpr,
      }),
    };
  }, [showPopupFrame, checkboxConfig, advancedText, dpr]);

  /**
   * Calculate Y position for Numbers of Rounds section
   * Mobile landscape: Positioned at bottom (side-by-side with checkboxes)
   * Windowed mode: Fixed position below Stop Autospin section (same in Basic/Advanced)
   * Other layouts: Positioned below Stop Autospin section
   */
  const numbersOfRoundsSectionY = useMemo(() => {
    if (!viewportConfig || !sectionHeadingStyle || !checkboxConfig) return 0;

    if (useTwoColumnCheckboxLayout) {
      // Mobile landscape: calculate from bottom
      const sectionHeadingHeight = sectionHeadingStyle.fontSize * 1.2;
      const numberOfRows = Math.ceil(
        AUTOPLAY_NUMBERS.length / buttonConfig.columns,
      );
      const buttonsGridHeight =
        numberOfRows * buttonConfig.height +
        (numberOfRows - 1) * buttonConfig.rowSpacing +
        buttonConfig.height;

      const totalSectionHeight = sectionHeadingHeight + buttonsGridHeight;
      return viewportConfig.viewportHeight - totalSectionHeight;
    }

    // Stacked layout
    if (showPopupFrame) {
      // Both Basic and Advanced: align buttons to bottom of content area
      const sectionHeadingHeight = viewportConfig.sectionFontSize;
      const sectionGap = viewportConfig.sectionGap;
      const numberOfRows = Math.ceil(AUTOPLAY_NUMBERS.length / 5); // 5 columns in windowed
      const buttonsGridHeight =
        numberOfRows * buttonConfig.height +
        (numberOfRows - 1) * buttonConfig.rowSpacing;
      const totalSectionHeight =
        sectionHeadingHeight + sectionGap + buttonsGridHeight;
      return (
        viewportConfig.viewportHeight -
        totalSectionHeight -
        buttonConfig.height * 0.3
      );
    }

    // Fullscreen mode: position below Stop Autospin section
    const stopAutospinTitleHeight = viewportConfig.sectionFontSize;
    const stopAutospinGap = viewportConfig.sectionGap * 2.5;
    const checkboxesHeight = checkboxConfig.spacing * 4 + checkboxConfig.size;
    const gapBetweenSections =
      viewportConfig.sectionGap *
      (device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? 1.95
        : 5);

    return (
      stopAutospinTitleHeight +
      stopAutospinGap +
      checkboxesHeight +
      gapBetweenSections
    );
  }, [
    viewportConfig,
    sectionHeadingStyle,
    buttonConfig,
    checkboxConfig,
    useTwoColumnCheckboxLayout,
    showPopupFrame,
    device,
    orientation,
  ]);

  // Calculate if buttons should be disabled (guard conditions)
  const buttonsDisabled = useMemo(() => {
    const hasWin = currentRound?.win && currentRound.win > 0;
    const winNotFullyDisplayed =
      hasWin && spinComplete && !counterAnimationFinished;

    return isSpinning || criticalAnimationPlaying || winNotFullyDisplayed;
  }, [
    isSpinning,
    criticalAnimationPlaying,
    currentRound,
    spinComplete,
    counterAnimationFinished,
  ]);

  /**
   * Calculate grid positions for autoplay number buttons
   * Windowed mode & mobile landscape: left-aligned
   * Other fullscreen modes: centered
   */
  const buttonPositions = useMemo(() => {
    if (!viewportConfig || !sectionHeadingStyle) return [];

    const verticalPadding = buttonConfig.height / 2 + 10;
    const positions = [];

    for (let i = 0; i < AUTOPLAY_NUMBERS.length; i++) {
      const row = Math.floor(i / buttonConfig.columns);
      const col = i % buttonConfig.columns;

      // Check if this item is on the last row and if the last row is incomplete
      const totalRows = Math.ceil(
        AUTOPLAY_NUMBERS.length / buttonConfig.columns,
      );
      const isLastRow = row === totalRows - 1;
      const itemsInLastRow =
        AUTOPLAY_NUMBERS.length % buttonConfig.columns || buttonConfig.columns;
      const lastRowOffset =
        isLastRow && itemsInLastRow < buttonConfig.columns
          ? ((buttonConfig.columns - itemsInLastRow) *
              (buttonConfig.width + buttonConfig.spacing)) /
            2
          : 0;

      let x;
      if (showPopupFrame || useTwoColumnCheckboxLayout) {
        // Left-aligned for windowed mode and mobile landscape
        x =
          buttonConfig.width / 2 +
          col * (buttonConfig.width + buttonConfig.spacing) +
          lastRowOffset;
      } else {
        // Centered for other fullscreen modes
        const effectiveCols =
          isLastRow && itemsInLastRow < buttonConfig.columns
            ? itemsInLastRow
            : buttonConfig.columns;
        x =
          -((effectiveCols - 1) * (buttonConfig.width + buttonConfig.spacing)) /
            2 +
          col * (buttonConfig.width + buttonConfig.spacing);
      }

      const y =
        verticalPadding + row * (buttonConfig.height + buttonConfig.rowSpacing);

      positions.push({ x: Math.round(x), y: Math.round(y) });
    }

    return positions;
  }, [
    buttonConfig,
    showPopupFrame,
    sectionHeadingStyle,
    viewportConfig,
    useTwoColumnCheckboxLayout,
  ]);

  /**
   * Handle autoplay number selection
   * Validates balance before starting autoplay, then closes popup
   */
  const handleNumberSelect = useCallback(
    (number) => {
      // Prevent starting new spin if ongoing spin, win lines displaying, or critical animation
      const hasWin = currentRound?.win && currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin && spinComplete && !counterAnimationFinished;

      if (isSpinning || winNotFullyDisplayed || criticalAnimationPlaying) {
        return;
      }

      // Allow autoplay start regardless of balance - API will handle insufficient credits
      soundManager.play(SOUND_NAME.button_click);

      dispatch(setAutoSpinCount(number));
      dispatch(startAutoPlay());
      handleClose();
    },
    [
      dispatch,
      handleClose,
      isSpinning,
      criticalAnimationPlaying,
      currentRound,
      spinComplete,
      counterAnimationFinished,
    ],
  );

  if (!popupFrameTexture || !viewportConfig || !titleStyle) {
    return null;
  }
  if (!popupActive && !isClosing) return null;

  return (
    <Container
      ref={containerRef}
      name="secondary_autoplay_overlay"
      zIndex={10}
      sortableChildren={true}
      eventMode="static"
    >
      {/* Overlay — catches clicks, blocks game underneath */}
      {showPopupFrame ? (
        <Graphics
          name="secondary_autoplay_click_outside"
          zIndex={0}
          draw={(g) => {
            g.clear();
            g.beginFill(0x000000, 0.001);
            g.drawRect(0, 0, dimensions.width, dimensions.height);
            g.endFill();
          }}
          alpha={overlayAlpha > 0 ? 1 : 0}
          eventMode="static"
          onpointertap={handleClose}
        />
      ) : (
        <Graphics
          name="secondary_autoplay_background"
          zIndex={0}
          draw={(g) => {
            g.clear();
            g.beginFill(0x161616, 1);
            g.drawRect(0, 0, dimensions.width, dimensions.height);
            g.endFill();
          }}
          alpha={overlayAlpha}
          // Fullscreen mode (mobile portrait/landscape, tablet portrait): the
          // popup may ONLY be closed via the X button — tapping the background
          // must not dismiss it. eventMode stays "static" so the fill still
          // absorbs taps (blocks the game underneath), it just no longer closes.
          eventMode="static"
        />
      )}

      {/* Popup Container */}
      <Container
        name="secondary_autoplay_popup"
        x={popupContainerPosition.x}
        y={popupContainerPosition.y}
        zIndex={1}
        sortableChildren={true}
      >
        {/* Popup Frame Sprite (Tablet Landscape/Desktop only) */}
        {showPopupFrame && (
          <Sprite
            texture={popupFrameTexture}
            anchor={0.5}
            width={popupSize.width}
            height={popupSize.height}
            roundPixels={true}
          />
        )}

        <Container
          name="secondary_autoplay_content_viewport"
          x={viewportConfig.x}
          y={viewportConfig.y}
          sortableChildren={true}
        >
          {/* Title — not interactive so it cannot steal taps from the X */}
          <Text
            text={titleText}
            style={titleStyle}
            anchor={{ x: 0, y: 0 }}
            x={0}
            y={0}
            roundPixels={true}
            eventMode="none"
          />

          {/* Basic/Advanced Mode Toggle Button (Windowed mode only) */}
          {showPopupFrame && modeButtonConfig && (
            <Container
              name="secondary_autoplay_mode_toggle"
              x={
                viewportConfig.maxWidth -
                modeButtonConfig.width / 2 -
                closeButtonConfig.width * closeButtonSizeMultiplier -
                22
              }
              y={modeButtonConfig.height / 2}
              zIndex={1}
              eventMode={buttonsDisabled ? "none" : "static"}
              cursor={buttonsDisabled ? "default" : "pointer"}
              onpointertap={handleModeToggle}
            >
              {/* Button Background */}
              <Sprite
                texture={buttonBackgroundTexture}
                anchor={0.5}
                width={modeButtonConfig.width}
                height={modeButtonConfig.height}
                roundPixels={true}
              />
              {/* Button Text */}
              <Text
                text={isAdvancedMode.current ? basicText : advancedText}
                style={modeButtonConfig.style}
                anchor={0.5}
                x={Math.round(modeButtonConfig.textX)}
                y={0}
                roundPixels={true}
              />
              {/* Triangle Indicator */}
              {triangleTexture &&
                (() => {
                  const triangleGap = modeButtonConfig.height * 0.15;

                  // Get actual displayed text (swapped: collapsed shows Advanced, expanded shows Basic)
                  const currentText = isAdvancedMode.current
                    ? basicText
                    : advancedText;

                  // Measure actual text width with current font size
                  const textMetrics = PIXI.TextMetrics.measureText(
                    currentText,
                    modeButtonConfig.style,
                  );
                  const actualTextWidth = textMetrics.width;

                  // Calculate triangle size maintaining aspect ratio
                  const triangleHeight = modeButtonConfig.triangleSize;
                  const triangleAspectRatio =
                    triangleTexture.width / triangleTexture.height;
                  const triangleWidth = triangleHeight * triangleAspectRatio;

                  // Position triangle right after the text
                  const triangleX =
                    modeButtonConfig.textX +
                    actualTextWidth / 2 +
                    triangleGap +
                    triangleWidth / 2;

                  // Rotate: 0 when Basic/expanded (pointing up), Math.PI when Advanced/collapsed (pointing down)
                  const rotation = isAdvancedMode.current ? Math.PI : 0;

                  return (
                    <Sprite
                      key={`triangle-${
                        isAdvancedMode.current ? "basic" : "advanced"
                      }`}
                      texture={triangleTexture}
                      anchor={0.5}
                      width={triangleWidth}
                      height={triangleHeight}
                      roundPixels={true}
                      x={triangleX}
                      y={0}
                      rotation={rotation}
                    />
                  );
                })()}
            </Container>
          )}

          {useTwoColumnCheckboxLayout ? (
            /**
             * Mobile Landscape Layout: Two-column side-by-side
             * Left: Stop Autospin conditions (55% width)
             * Right: Numbers of Rounds buttons (45% width)
             */
            <>
              {/* Left Column - Stop Autospin Section (55% width) - Always shown in fullscreen */}
              <Container x={0} y={viewportConfig.titleYOffset}>
                <Text
                  text={stopAutospinText}
                  style={sectionHeadingStyle}
                  anchor={{ x: 0, y: 0 }}
                  x={0}
                  y={0}
                  resolution={2}
                />

                {/* Checkboxes Container */}
                <Container
                  y={
                    viewportConfig.sectionFontSize +
                    viewportConfig.sectionGap * 2.5
                  }
                >
                  <Checkbox
                    translationKey="ui_autospins_on_any_win"
                    checked={stopOnAnyWin}
                    onChange={handleOnAnyWinChange}
                    x={checkboxConfig.xOffset}
                    y={0}
                    sizeMultiplier={
                      checkboxConfig.twoColumnCheckboxSizeMultiplier
                    }
                    labelFontSize={checkboxConfig.labelFontSize}
                    containerWidth={
                      (viewportConfig.maxWidth - twoColumnGap) *
                        twoColumnLayoutConfig.leftRatio -
                      checkboxConfig.xOffset
                    }
                    referenceInputWidth={checkboxConfig.inputWidth}
                  />
                  <Checkbox
                    translationKey="ui_autospins_if_bonus_won"
                    checked={stopOnBonusGame}
                    onChange={handleBonusGameChange}
                    x={checkboxConfig.xOffset}
                    y={checkboxConfig.spacing}
                    sizeMultiplier={
                      checkboxConfig.twoColumnCheckboxSizeMultiplier
                    }
                    labelFontSize={checkboxConfig.labelFontSize}
                    containerWidth={
                      (viewportConfig.maxWidth - twoColumnGap) *
                        twoColumnLayoutConfig.leftRatio -
                      checkboxConfig.xOffset
                    }
                    referenceInputWidth={checkboxConfig.inputWidth}
                  />
                  <CheckboxWithInput
                    translationKey="ui_autospins_if_single_win"
                    checked={stopOnSingleWinExceeds}
                    onChange={handleSingleWinChange}
                    value={singleWinExceedsValue}
                    onValueChange={(value) => {
                      dispatch(setSingleWinExceedsAmount(value));
                      if (value && value !== "") {
                        dispatch(setStopOnSingleWinExceeds(true));
                      } else if (value === "" && stopOnSingleWinExceeds) {
                        dispatch(setStopOnSingleWinExceeds(false));
                      }
                    }}
                    x={checkboxConfig.xOffset}
                    y={checkboxConfig.spacing * 2}
                    sizeMultiplier={checkboxConfig.twoColumnInputSizeMultiplier}
                    labelFontSize={checkboxConfig.labelFontSize}
                    inputWidth={checkboxConfig.inputWidth}
                    inputHeight={checkboxConfig.inputHeight}
                    containerWidth={
                      (viewportConfig.maxWidth - twoColumnGap) *
                        twoColumnLayoutConfig.leftRatio -
                      checkboxConfig.xOffset
                    }
                    inputId="singleWinExceeds"
                    focusedInput={focusedInput}
                    onFocusChange={setFocusedInput}
                  />
                  <CheckboxWithInput
                    translationKey="ui_autospins_if_increases"
                    checked={stopOnBalanceIncreases}
                    onChange={handleBalanceIncreasesChange}
                    value={balanceIncreasesValue}
                    onValueChange={(value) => {
                      dispatch(setBalanceIncreasesAmount(value));
                      if (value && value !== "") {
                        dispatch(setStopOnBalanceIncreases(true));
                        dispatch(setStopOnAnyWin(false));
                      } else if (value === "" && stopOnBalanceIncreases) {
                        dispatch(setStopOnBalanceIncreases(false));
                      }
                    }}
                    x={checkboxConfig.xOffset}
                    y={checkboxConfig.spacing * 3}
                    sizeMultiplier={checkboxConfig.twoColumnInputSizeMultiplier}
                    labelFontSize={checkboxConfig.labelFontSize}
                    inputWidth={checkboxConfig.inputWidth}
                    inputHeight={checkboxConfig.inputHeight}
                    containerWidth={
                      (viewportConfig.maxWidth - twoColumnGap) *
                        twoColumnLayoutConfig.leftRatio -
                      checkboxConfig.xOffset
                    }
                    inputId="balanceIncreases"
                    focusedInput={focusedInput}
                    onFocusChange={setFocusedInput}
                  />
                  <CheckboxWithInput
                    translationKey="ui_autospins_if_decreases"
                    checked={stopOnBalanceDecreases}
                    onChange={(checked) => {
                      dispatch(setStopOnBalanceDecreases(checked));
                      if (checked) {
                        dispatch(setStopOnAnyWin(false));
                      }
                      if (!checked) {
                        dispatch(setBalanceDecreasesAmount(""));
                        setFocusedInput((prev) =>
                          prev === "balanceDecreases" ? null : prev,
                        );
                      }
                    }}
                    value={balanceDecreasesValue}
                    onValueChange={(value) => {
                      dispatch(setBalanceDecreasesAmount(value));
                      // Auto-check/uncheck based on value (only when typing, not when checkbox toggles)
                      if (value && value !== "") {
                        dispatch(setStopOnBalanceDecreases(true));
                        dispatch(setStopOnAnyWin(false));
                      } else if (value === "" && stopOnBalanceDecreases) {
                        dispatch(setStopOnBalanceDecreases(false));
                      }
                    }}
                    x={checkboxConfig.xOffset}
                    y={checkboxConfig.spacing * 4}
                    sizeMultiplier={checkboxConfig.twoColumnInputSizeMultiplier}
                    labelFontSize={checkboxConfig.labelFontSize}
                    inputWidth={checkboxConfig.inputWidth}
                    inputHeight={checkboxConfig.inputHeight}
                    containerWidth={
                      (viewportConfig.maxWidth - twoColumnGap) *
                        twoColumnLayoutConfig.leftRatio -
                      checkboxConfig.xOffset
                    }
                    inputId="balanceDecreases"
                    focusedInput={focusedInput}
                    onFocusChange={setFocusedInput}
                  />
                </Container>
              </Container>

              {/* Right Column - Numbers of Rounds Section */}
              <Container
                x={
                  (viewportConfig.maxWidth - twoColumnGap) *
                    twoColumnLayoutConfig.leftRatio +
                  twoColumnGap
                }
                y={viewportConfig.titleYOffset}
              >
                <Text
                  text={numbersOfRoundsText}
                  style={sectionHeadingStyle}
                  anchor={{ x: 0, y: 0 }}
                  x={0}
                  y={0}
                  roundPixels={true}
                />

                {/* Autoplay Number Buttons Grid */}
                <Container
                  x={0}
                  y={viewportConfig.sectionFontSize + viewportConfig.sectionGap}
                >
                  {AUTOPLAY_NUMBERS.map((number, index) => {
                    const position = buttonPositions[index];
                    if (!position) return null;

                    const isDisabled = balance < bet || buttonsDisabled;
                    const displayText =
                      number === Infinity ? "∞" : number.toString();

                    return (
                      <BetButton
                        key={number}
                        x={position.x}
                        y={position.y}
                        text={displayText}
                        onClick={() => handleNumberSelect(number)}
                        disabled={isDisabled}
                        active={false}
                        width={buttonConfig.width}
                        height={buttonConfig.height}
                        fontSize={buttonConfig.fontSize}
                      />
                    );
                  })}
                </Container>
              </Container>
            </>
          ) : (
            /**
             * Stacked Layout: For all devices except mobile landscape
             * Stop Autospin section on top (only in Advanced mode for windowed), Numbers of Rounds section below
             */
            <>
              {/* Stop Autospin Section - conditionally rendered in windowed mode */}
              {(!showPopupFrame || isAdvancedMode.current) && (
                <Container
                  y={
                    viewportConfig.titleYOffset +
                    viewportConfig.sectionGap *
                      (device === PixiSettings.DEVICE.MOBILE &&
                      orientation === PixiSettings.ORIENTATION.PORTRAIT
                        ? 1.35
                        : 1.5)
                  }
                >
                  <Text
                    text={stopAutospinText}
                    style={sectionHeadingStyle}
                    anchor={{ x: 0, y: 0 }}
                    x={0}
                    y={0}
                  />

                  {/* Checkboxes Container */}
                  <Container
                    y={
                      viewportConfig.sectionFontSize +
                      viewportConfig.sectionGap *
                        (device === PixiSettings.DEVICE.MOBILE &&
                        orientation === PixiSettings.ORIENTATION.PORTRAIT
                          ? 2.25
                          : 4)
                    }
                  >
                    <Checkbox
                      translationKey="ui_autospins_on_any_win"
                      checked={stopOnAnyWin}
                      onChange={handleOnAnyWinChange}
                      x={checkboxConfig.xOffset}
                      y={0}
                      sizeMultiplier={checkboxConfig.stackedSizeMultiplier}
                      labelFontSize={checkboxConfig.labelFontSize}
                      containerWidth={
                        viewportConfig.maxWidth - checkboxConfig.xOffset
                      }
                    />
                    <Checkbox
                      translationKey="ui_autospins_if_bonus_won"
                      checked={stopOnBonusGame}
                      onChange={handleBonusGameChange}
                      x={checkboxConfig.xOffset}
                      y={checkboxConfig.spacing}
                      sizeMultiplier={checkboxConfig.stackedSizeMultiplier}
                      labelFontSize={checkboxConfig.labelFontSize}
                      containerWidth={
                        viewportConfig.maxWidth - checkboxConfig.xOffset
                      }
                    />
                    <CheckboxWithInput
                      translationKey="ui_autospins_if_single_win"
                      checked={stopOnSingleWinExceeds}
                      onChange={handleSingleWinChange}
                      value={singleWinExceedsValue}
                      onValueChange={(value) => {
                        dispatch(setSingleWinExceedsAmount(value));
                        if (value && value !== "") {
                          dispatch(setStopOnSingleWinExceeds(true));
                        } else if (value === "" && stopOnSingleWinExceeds) {
                          dispatch(setStopOnSingleWinExceeds(false));
                        }
                      }}
                      x={checkboxConfig.xOffset}
                      y={checkboxConfig.spacing * 2}
                      sizeMultiplier={checkboxConfig.stackedSizeMultiplier}
                      labelFontSize={checkboxConfig.labelFontSize}
                      inputWidth={checkboxConfig.inputWidth}
                      inputHeight={checkboxConfig.inputHeight}
                      containerWidth={
                        viewportConfig.maxWidth - checkboxConfig.xOffset
                      }
                      inputId="singleWinExceeds"
                      focusedInput={focusedInput}
                      onFocusChange={setFocusedInput}
                    />
                    <CheckboxWithInput
                      translationKey="ui_autospins_if_increases"
                      checked={stopOnBalanceIncreases}
                      onChange={handleBalanceIncreasesChange}
                      value={balanceIncreasesValue}
                      onValueChange={(value) => {
                        dispatch(setBalanceIncreasesAmount(value));
                        if (value && value !== "") {
                          dispatch(setStopOnBalanceIncreases(true));
                          dispatch(setStopOnAnyWin(false));
                        } else if (value === "" && stopOnBalanceIncreases) {
                          dispatch(setStopOnBalanceIncreases(false));
                        }
                      }}
                      x={checkboxConfig.xOffset}
                      y={checkboxConfig.spacing * 3}
                      sizeMultiplier={checkboxConfig.stackedSizeMultiplier}
                      labelFontSize={checkboxConfig.labelFontSize}
                      inputWidth={checkboxConfig.inputWidth}
                      inputHeight={checkboxConfig.inputHeight}
                      containerWidth={
                        viewportConfig.maxWidth - checkboxConfig.xOffset
                      }
                      inputId="balanceIncreases"
                      focusedInput={focusedInput}
                      onFocusChange={setFocusedInput}
                    />
                    <CheckboxWithInput
                      translationKey="ui_autospins_if_decreases"
                      checked={stopOnBalanceDecreases}
                      onChange={(checked) => {
                        dispatch(setStopOnBalanceDecreases(checked));
                        if (checked) {
                          dispatch(setStopOnAnyWin(false));
                        }
                        if (!checked) {
                          dispatch(setBalanceDecreasesAmount(""));
                          setFocusedInput((prev) =>
                            prev === "balanceDecreases" ? null : prev,
                          );
                        }
                      }}
                      value={balanceDecreasesValue}
                      onValueChange={(value) => {
                        dispatch(setBalanceDecreasesAmount(value));
                        // Auto-check/uncheck based on value (only when typing, not when checkbox toggles)
                        if (value && value !== "") {
                          dispatch(setStopOnBalanceDecreases(true));
                          dispatch(setStopOnAnyWin(false));
                        } else if (value === "" && stopOnBalanceDecreases) {
                          dispatch(setStopOnBalanceDecreases(false));
                        }
                      }}
                      x={checkboxConfig.xOffset}
                      y={checkboxConfig.spacing * 4}
                      sizeMultiplier={checkboxConfig.stackedSizeMultiplier}
                      labelFontSize={checkboxConfig.labelFontSize}
                      inputWidth={checkboxConfig.inputWidth}
                      inputHeight={checkboxConfig.inputHeight}
                      containerWidth={
                        viewportConfig.maxWidth - checkboxConfig.xOffset
                      }
                      inputId="balanceDecreases"
                      focusedInput={focusedInput}
                      onFocusChange={setFocusedInput}
                    />
                  </Container>
                </Container>
              )}

              {/* Numbers of Rounds Section */}
              <Container
                key={`numbers-section-${useTwoColumnCheckboxLayout}`}
                y={viewportConfig.titleYOffset + numbersOfRoundsSectionY}
              >
                <Text
                  text={numbersOfRoundsText}
                  style={sectionHeadingStyle}
                  anchor={{ x: 0, y: 0 }}
                  x={0}
                  y={0}
                  roundPixels={true}
                />

                {/* Autoplay Number Buttons Grid */}
                <Container
                  key={`buttons-grid-${useTwoColumnCheckboxLayout}`}
                  x={showPopupFrame ? 0 : viewportConfig.maxWidth / 2}
                  y={viewportConfig.sectionFontSize + viewportConfig.sectionGap}
                >
                  {AUTOPLAY_NUMBERS.map((number, index) => {
                    const position = buttonPositions[index];
                    if (!position) return null;

                    const isDisabled = balance < bet || buttonsDisabled;
                    const displayText =
                      number === Infinity ? "∞" : number.toString();

                    return (
                      <BetButton
                        key={number}
                        x={position.x}
                        y={position.y}
                        text={displayText}
                        onClick={() => handleNumberSelect(number)}
                        disabled={isDisabled}
                        active={false}
                        width={buttonConfig.width}
                        height={buttonConfig.height}
                        fontSize={buttonConfig.fontSize}
                      />
                    );
                  })}
                </Container>
              </Container>
            </>
          )}

          {/* Close — original title-row position; rendered last + zIndex 10 so it
              receives taps the same way checkboxes do (above title/content). */}
          <Container
            name="secondary_autoplay_close_button"
            x={
              viewportConfig.maxWidth -
              (closeButtonConfig.width * closeButtonSizeMultiplier) / 2 -
              (showPopupFrame ? 0 : 2)
            }
            y={closeButtonLocalY}
            zIndex={10}
          >
            <ButtonMenuClose
              onClose={handleClose}
              x={0}
              y={0}
              isSettings={true}
              sizeMultiplier={closeButtonSizeMultiplier}
            />
          </Container>
        </Container>
      </Container>

    </Container>
  );
};

export default AutoplayPopup;
